import { Component, Input, ElementRef, ViewChild } from '@angular/core';

import { Observable } from 'rxjs';
import { MessageService } from 'primeng/api';

import { AIChatCompletionApiModel, AIChatCompletionMessageApiModel } from '../../../../api/ai/model/AI-chat-completion-api.model';
import { ChatMessageTypeModel, ChatRole } from '../../../../api/ai/enums/AI-chat-completion.enum';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { ConfigService } from '../../../../Core/Config/services/config.service';
import { ResizeDirectionEnum } from '../../../Forms/components/TextareaInput/textarea-input.component';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { AbstractAIHelperDialogService } from '../../services/abstract-AI-helper-dialog.service';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { AbstractAiDialogComponent } from '../AbstractAIDialog/Abstract-AI-dialog.component';
import { AiSettingFormModel } from '../../../Forms/models/ai-setting-form.model';
import { AiQuickTipEnums } from '../../../Forms/enums/ai-quick-tip.enum';

@Component({
    selector: 'agpr-ai-helper-dialog',
    templateUrl: './AI-helper-dialog.component.html',
    styleUrls: ['./AI-helper-dialog.component.scss'],
    standalone: false
})

export class AIHelperDialogComponent extends AbstractAiDialogComponent {
    readonly buttonEnum = ButtonTypesEnum;
    readonly resizeDirectionEnum = ResizeDirectionEnum;
    readonly minPanelWidth = 1180;
    readonly maxPanelWidth = 1380;

    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;
    @Input() aiReferenceId: string;
    @Input() title: string = '';
    @Input() editState: boolean = false;

    aiSettings: AiSettingFormModel = new AiSettingFormModel();

    hideSlideOverlay: boolean = false;
    agreedDisclaimer: boolean = false;
    loading: boolean = false;
    doneStreaming = true;
    hasGenerated: boolean = false;
    hasKeyMessage: boolean = false;
    generatedContent: string = '';

    @ViewChild('quickTipContainer') quickTipContainer: ElementRef;

    quickTipInfo = {
        header: '',
        hints: []
    };

    constructor(
        coreDialogService: CoreDialogService,
        public localizationService: LocalizationService,
        public configService: ConfigService,
        public aiHelperDialogService: AbstractAIHelperDialogService,
        public messageService: MessageService
    ) {
        super(coreDialogService, 'AIHelper');
        this.aiSettings.formatConfig = this.aiHelperDialogService.getConfig();
    }

    generate(): void {
        this.generatedContent = '';
        this.loading = true;
        this.doneStreaming = false;

        this.fetchData(this.configService.getConfig().apiUrl + '/ai/chat/completions', undefined, this.aiReferenceId);
    }

    handleError() {
        this.messageService.add(
            {
                severity: messageSeverityEnum.error,
                summary: this.localizationService.transform('Common.dialog.aiHelper.error.title'),
                detail: this.localizationService.transform('Common.dialog.aiHelper.error.description')
            }
        );
        this.loading = false;
        this.hasGenerated = true;
    }

    getRequestModel(): AIChatCompletionApiModel {
        const deduplicatedKeyMessages = Array.from(new Set(this.aiSettings.keyMessages.filter(msg => msg.trim().length)));

        const message: AIChatCompletionMessageApiModel = {
            role: ChatRole.USER,
            chatMessageType: ChatMessageTypeModel.CONTENT_GENERATION,
            message : JSON.stringify({
                keyMessages: deduplicatedKeyMessages,
                quotes: this.aiSettings.quotes.map(q => q.getApiModel()).filter(q => q != null),
                format: this.aiSettings.formatConfig.defaultFormat,
                languageTag: this.aiSettings.language,
                customLanguage: this.aiSettings.customLanguage,
                tone: this.aiSettings.tone,
                customTone: this.aiSettings.customTone,
                targetAudience: this.aiSettings.targetAudience,
                customTargetAudience: this.aiSettings.customTargetAudience,
                purpose: this.aiSettings.purpose,
                customPurpose: this.aiSettings.customPurpose,
                length: this.aiSettings.length
            })
        };
        return { messages: [message]};
    }

    apply(): void {
        this.closeDialog(this.generatedContent);
    }

    showQuickTip(type: AiQuickTipEnums) {
        if (this.generatedContent.length || !this.quickTipContainer) {
            return;
        }

        // show div
        this.quickTipContainer.nativeElement.style.display = 'block';
        this.quickTipInfo.header = this.aiHelperDialogService.getQuickTipHeader(type);
        this.quickTipInfo.hints = this.aiHelperDialogService.getQuickTipHints(type);
    }

    validateAiSettings(): void {
        this.hasKeyMessage = this.aiSettings.keyMessages.filter(keyMessage => keyMessage.trim().length).length !== 0;
    }
}
